'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastPayload {
  type: ToastType;
  title?: string;
  message: string;
  /** Куда перейти по клику (например, открыть чат). */
  href?: string;
  /** Иконка/аватар (например, из мессенджера). */
  iconUrl?: string;
  durationMs?: number;
}

export interface ToastItem extends ToastPayload {
  id: string;
  createdAt: number;
}

interface ToastContextValue {
  /** Текущее уведомление для шапки (одно за раз). */
  active: ToastItem | null;
  push: (toast: ToastPayload) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function generateId() {
  // crypto.randomUUID доступен не во всех средах (особенно на старых браузерах)
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID() as string;
  }
  return `toast_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getDefaultDuration(type: ToastType) {
  switch (type) {
    case 'error':
      return 5000;
    case 'warning':
      return 6500;
    case 'success':
      return 4500;
    case 'info':
    default:
      return 5000;
  }
}

export function getToastStyles(type: ToastType) {
  switch (type) {
    case 'success':
      return {
        border: 'border-site-success/40',
        bg: 'bg-site-success/10',
        icon: '✅',
        title: 'text-site-success',
        text: 'text-site-success/90',
      };
    case 'error':
      return {
        border: 'border-site-danger/40',
        bg: 'bg-site-danger/10',
        icon: '❌',
        title: 'text-site-danger',
        text: 'text-site-danger/90',
      };
    case 'warning':
      return {
        border: 'border-site-warning/40',
        bg: 'bg-site-warning/10',
        icon: '⚠️',
        title: 'text-site-warning',
        text: 'text-site-warning/90',
      };
    case 'info':
    default:
      return {
        border: 'border-site-accent/40',
        bg: 'bg-site-accent/10',
        icon: 'ℹ️',
        title: 'text-site-accent',
        text: 'text-site-accent/90',
      };
  }
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  // Комментарий на русском: уведомления показываем ТОЛЬКО в шапке (одна штука за раз), чтобы ничего не "прыгало" по высоте страницы.
  const [active, setActive] = useState<ToastItem | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const dismiss = useCallback((id: string) => {
    // Комментарий на русском: закрываем только если это текущий активный тост (защита от гонок таймеров).
    setActive((prev) => {
      if (!prev || prev.id !== id) return prev;
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      return null;
    });
  }, []);

  const clear = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setActive(null);
  }, []);

  const push = useCallback(
    (toast: ToastPayload) => {
      const id = generateId();
      // Комментарий на русском: ошибки всегда показываем 5 секунд (единое правило по проекту).
      const durationMs =
        toast.type === 'error'
          ? 5000
          : typeof toast.durationMs === 'number'
            ? toast.durationMs
            : getDefaultDuration(toast.type);
      const item: ToastItem = {
        ...toast,
        id,
        createdAt: Date.now(),
        durationMs,
      };

      // Комментарий на русском: новое уведомление "перебивает" старое (в шапке показываем только одно).
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      setActive(item);

      const timeoutId = window.setTimeout(() => {
        // Комментарий на русском: не закрываем, если за это время уже показали другое уведомление.
        setActive((prev) => (prev?.id === id ? null : prev));
      }, durationMs);
      timeoutRef.current = timeoutId;
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ active, push, dismiss, clear }), [active, push, dismiss, clear]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

